import apiClient from '@/lib/api';

/** Trial risk, scored live from what the trial has produced so far. */
export interface TrialRiskInfo {
  /** TrialRiskLevel from the API: high_risk | medium_risk | healthy. */
  level: string;
  completedSubmissions: number;
  appointments: number;
}

export interface AdminAgencyRecord {
  id: string; // The publicId of the agency
  name: string;
  planId: number | null;
  planName: string | null;
  subaccountCount: number;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  stripeStatus: string | null;
  /** Derived billing bucket (BillingFilter). Use this for the status badge, not stripeStatus. */
  billingStatus: string;
  /** Why the agency has manual access (billing status "manual"), as typed in the edit form. */
  manualAccessReason?: string | null;
  /** Derived engagement stage (LifecycleStage), same value the CRM is synced with. */
  lifecycleStage: string;
  /** Only trialing agencies are scored; null for everyone else. The list returns it, the detail does not. */
  trialRisk?: TrialRiskInfo | null;
  stripeTrialEndsAtUtc?: string | null;
  formsCount: number;
  /** The list returns only completedSubmissionsCount; the detail carries the rest. */
  submissionsCount?: number;
  completedSubmissionsCount?: number;
  partialSubmissionsCount?: number;
  leadsCount?: number;
  completedLeadsCount?: number;
  partialLeadsCount?: number;
  lastActivityAt: string | null;
  createdAtUtc: string;
  superAdminName: string | null;
  superAdminEmail: string | null;
  superAdminId: string | null;
  brandingPolicy?: 'legacy' | 'always_on';
}

/** GET /admin/agencies/:id/setup-wizard: how far the agency got in the dashboard's setup wizard (steps 1 to 6). */
export interface AgencySetupWizard {
  started: boolean;
  startedAt: string | null;
  furthestStep: number | null;
  lastStep: number | null;
  lastActivityAt: string | null;
  leftEarlyAtStep: number | null;
  testLeadSent: boolean;
  publishedAt: string | null;
  status: 'not_started' | 'in_progress' | 'left_early' | 'finished';
}

/** Read from the cancel and downgrade events, since the agency's own reason is wiped when access ends. */
export interface AgencyChurn {
  /** CHURN_TYPE_CONFIG key: trial_churn | paid_churn | churn_unknown | cancelling_trial | cancelling_paid. */
  type: string;
  cancelledAtUtc: string | null;
  reason: string | null;
  /** When access ended, or ends for a cancelling agency. */
  endsAtUtc: string | null;
  /** The plan they were on, or are on while cancelling. */
  planName: string | null;
}

export interface AgencyDetailRecord extends AdminAgencyRecord {
  slug: string;
  logoUrl: string | null;
  /** A paid plan with no live Stripe subscription behind it (backend isManualAccess). */
  manualAccess: boolean;
  stripeCurrentPeriodEndUtc: string | null;
  cancelAtPeriodEnd: boolean;
  cancellationReason: string | null;
  /** Why and when they left, for a cancelled, trial-expired or cancelling agency; null otherwise. */
  churn: AgencyChurn | null;
  /** Extra subaccounts bought on Solo, and what they bill per month in dollars. */
  extraSubaccounts: number;
  extraSubaccountsMonthlyAmount: number;
  stripeTrialStartsAtUtc: string | null;
  stripeTrialEndsAtUtc: string | null;
  trialReminderSentAtUtc: string | null;
  onboardingStep: string;
  onboardingAnswers: any;
  onboardingCompletedAtUtc: string | null;
  affonsoToken: string | null;
  referredByName: string | null;
  /** Affonso confirmed, on this load, that no affiliate has affonsoToken */
  affonsoTokenUnmatched: boolean;
  referredByEmail: string | null;
  affonsoData?: any;
  metadata?: any;
  downgradeOfferClaimedAtUtc?: string | null;
  hasShadowAccess?: boolean;
  brandingPolicyEffective?: 'legacy' | 'always_on';
  signupSourceForm?: {
    publicId: string; name: string; subaccountName: string; agencyPublicId: string; agencyName: string;
  } | null;
  formsCount: number;
  submissionsCount: number;
  completedSubmissionsCount?: number;
  partialSubmissionsCount?: number;
  leadsCount: number;
  completedLeadsCount?: number;
  partialLeadsCount?: number;
  appointmentsCount: number;
  /** Completed submissions this UTC month; limit null = unlimited plan. */
  submissionUsage?: SubmissionUsage;
}

export interface SubmissionUsage {
  used: number;
  limit: number | null;
}

export interface AgencySubaccount {
  id: string;
  name: string;
  slug: string;
  status: string;
  createdAtUtc: string;
  formsCount: number;
  submissionsCount: number;
  completedSubmissionsCount?: number;
  partialSubmissionsCount?: number;
  leadsCount: number;
  completedLeadsCount?: number;
  partialLeadsCount?: number;
  qualifiedCount: number;
  appointmentsCount: number;
  optinRate: number;
  qualifiedRate: number;
  bookCallRate: number;
  teamMemberCount: number;
}

export interface AgencyTeamMember {
  id: string;
  name: string;
  email: string;
  role: string;
  status?: string;
  level: 'agency' | 'subaccount';
  subaccountName: string | null;
  createdAtUtc: string;
}

export interface AgencyEventRecord {
  id: string;
  eventType: string;
  metadata: any;
  createdAtUtc: string;
  user: {
    id: number;
    email: string;
    firstName: string | null;
    lastName: string | null;
  } | null;
}

export interface PaginationMeta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface PaginatedAgenciesResponse {
  data: AdminAgencyRecord[];
  meta: PaginationMeta;
}

export interface PaginatedSubaccountsResponse {
  data: AgencySubaccount[];
  meta: PaginationMeta;
}

export interface PaginatedTeamResponse {
  data: AgencyTeamMember[];
  meta: PaginationMeta;
}

/** The Activity Log's tabs besides All (back-end-next/src/lib/platform-event-categories.ts). */
export type ActivityCategory = 'onboarding' | 'usage' | 'building' | 'billing' | 'emails';

export type ActivityCategoryCounts = Record<'all' | ActivityCategory, number>;

export interface PaginatedEventsResponse {
  data: AgencyEventRecord[];
  meta: PaginationMeta & { categoryCounts: ActivityCategoryCounts };
}

/** GET /admin/agencies/:id/activity: the strip at the top of the Activity Log, computed live. */
export interface AgencyActivity {
  /** The latest last seen among the agency's members, backoffice staff left out. */
  lastSeenAtUtc: string | null;
  /** Distinct UTC dates ('YYYY-MM-DD', oldest first) with an active_day event, in the 30 UTC days ending today. */
  activeDays: string[];
  /** The UTC date of the agency's first active_day event, or null when there is none. */
  trackedSince: string | null;
  lastLogin: { method: string | null; atUtc: string; userName: string | null } | null;
  publishedForms: number;
}

export interface AgencyFilters {
  status?: string;
  stage?: string;
  /** Refines churn into trial vs paid vs unknown. Only meaningful alongside a churn status/stage. */
  churnType?: string;
  planId?: string;
  /** Trial risk level; only trialing agencies carry one, so it implies the trialing status. */
  trialRisk?: string;
  hasStripe?: string;
  dateFrom?: string;
  dateTo?: string;
  /** Bounds on the completed submissions count. Either end may be left open. */
  minSubmissions?: string;
  maxSubmissions?: string;
}

/**
 * Writes the list filters onto a query string. Shared by the list request and
 * the CSV export so the file always covers the rows the operator is looking at.
 */
function applyAgencyFilters(params: URLSearchParams, filters: AgencyFilters): void {
  if (filters.status) params.set('status', filters.status);
  if (filters.stage) params.set('stage', filters.stage);
  if (filters.churnType) params.set('churnType', filters.churnType);
  if (filters.planId) params.set('planId', filters.planId);
  if (filters.trialRisk) params.set('trialRisk', filters.trialRisk);
  if (filters.hasStripe) params.set('hasStripe', filters.hasStripe);
  if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
  if (filters.dateTo) params.set('dateTo', filters.dateTo);
  if (filters.minSubmissions) params.set('minSubmissions', filters.minSubmissions);
  if (filters.maxSubmissions) params.set('maxSubmissions', filters.maxSubmissions);
}

export interface AgencyExportResult {
  rows: number;
  truncated: boolean;
  rowLimit: number;
}

export interface StripeSubscriptionPreview {
  status: string;
  customerId: string;
  /** The plan the subscription's price belongs to, or null if it maps to none. */
  matchedPlan: { id: number; displayName: string } | null;
}

export const agenciesService = {
  /**
   * Fetch paginated list of agencies with optional search query and filters
   */
  getAgencies: async (
    page: number = 1,
    limit: number = 20,
    search: string = '',
    sortBy: string = 'createdAtUtc',
    sortDir: string = 'desc',
    filters: AgencyFilters = {},
    signal?: AbortSignal
  ): Promise<PaginatedAgenciesResponse> => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(search ? { search } : {}),
      sortBy,
      sortDir,
    });

    applyAgencyFilters(params, filters);

    const response = await apiClient.get<{ data: AdminAgencyRecord[], meta: any }>(
      `/admin/agencies?${params.toString()}`,
      { signal }
    );

    return response.data as unknown as PaginatedAgenciesResponse;
  },

  /**
   * Download the filtered agency list as a CSV.
   *
   * The server builds the file (one row per agency, every column we hold at that
   * grain), so this only hands the blob to the browser. Paging and sort are
   * deliberately not sent: an export is the whole filtered set, narrowed to
   * `ids` when specific rows are ticked, or minus `excludeIds` when "select all
   * matching" is on.
   */
  exportAgencies: async (
    search: string = '',
    filters: AgencyFilters = {},
    selection: { ids?: string[]; excludeIds?: string[] } = {}
  ): Promise<AgencyExportResult> => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    applyAgencyFilters(params, filters);
    // Ticked rows narrow the filtered set; excluded ones are the rows un-ticked
    // while "select all matching" is on. Never both at once.
    if (selection.ids?.length) params.set('ids', selection.ids.join(','));
    if (selection.excludeIds?.length) {
      params.set('excludeIds', selection.excludeIds.join(','));
    }

    const response = await apiClient.get(`/admin/agencies/export?${params.toString()}`, {
      responseType: 'blob',
      // The export aggregates across the whole filtered table, which routinely
      // outruns the client's 30s default.
      timeout: 180000,
    });

    const disposition = response.headers['content-disposition'] as string | undefined;
    const match = disposition?.match(/filename="([^"]+)"/);
    const filename = match?.[1] || `agencies-export-${Date.now()}.csv`;

    const url = URL.createObjectURL(response.data as Blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);

    return {
      rows: Number(response.headers['x-export-rows'] ?? 0),
      truncated: response.headers['x-export-truncated'] === 'true',
      rowLimit: Number(response.headers['x-export-row-limit'] ?? 0),
    };
  },

  /**
   * Fetch a single agency by ID
   */
  getAgency: async (id: string): Promise<AgencyDetailRecord> => {
    const response = await apiClient.get<{ data: AgencyDetailRecord }>(`/admin/agencies/${id}`);
    return response.data.data;
  },

  getAgencySetupWizard: async (id: string): Promise<AgencySetupWizard> => {
    const response = await apiClient.get<{ data: AgencySetupWizard }>(`/admin/agencies/${id}/setup-wizard`);
    return response.data.data;
  },

  /**
   * Set an agency's completed-submission count for the current month (0 resets it).
   */
  setSubmissionUsage: async (agencyId: string, used: number): Promise<SubmissionUsage> => {
    const response = await apiClient.put<{ data: SubmissionUsage }>(
      `/admin/agencies/${agencyId}/submission-usage`,
      { used }
    );
    return response.data.data;
  },

  /**
   * Fetch paginated subaccounts for an agency
   */
  getAgencySubaccounts: async (
    id: string,
    params: { page?: number; search?: string; dateFrom?: string; dateTo?: string; unique?: boolean } = {}
  ): Promise<PaginatedSubaccountsResponse> => {
    const searchParams = new URLSearchParams();
    searchParams.set('page', String(params.page || 1));
    if (params.search) searchParams.set('search', params.search);
    if (params.dateFrom) searchParams.set('dateFrom', params.dateFrom);
    if (params.dateTo) searchParams.set('dateTo', params.dateTo);
    if (params.unique !== undefined) searchParams.set('unique', String(params.unique));

    const response = await apiClient.get(`/admin/agencies/${id}/subaccounts?${searchParams.toString()}`);
    return response.data as unknown as PaginatedSubaccountsResponse;
  },

  /**
   * Fetch paginated team members for an agency
   */
  getAgencyTeam: async (
    id: string,
    params: { page?: number; search?: string; role?: string } = {}
  ): Promise<PaginatedTeamResponse> => {
    const searchParams = new URLSearchParams();
    searchParams.set('page', String(params.page || 1));
    if (params.search) searchParams.set('search', params.search);
    if (params.role && params.role !== 'all') searchParams.set('role', params.role);

    const response = await apiClient.get(`/admin/agencies/${id}/team?${searchParams.toString()}`);
    return response.data as unknown as PaginatedTeamResponse;
  },

  /**
   * Fetch paginated platform events for an agency
   */
  getAgencyEvents: async (
    id: string,
    params: { page?: number; limit?: number; category?: ActivityCategory } = {}
  ): Promise<PaginatedEventsResponse> => {
    const searchParams = new URLSearchParams();
    searchParams.set('page', String(params.page || 1));
    searchParams.set('limit', String(params.limit || 20));
    if (params.category) searchParams.set('category', params.category);

    const response = await apiClient.get(`/admin/agencies/${id}/events?${searchParams.toString()}`);
    return response.data as unknown as PaginatedEventsResponse;
  },

  getAgencyActivity: async (id: string): Promise<AgencyActivity> => {
    const response = await apiClient.get<{ data: AgencyActivity }>(`/admin/agencies/${id}/activity`);
    return response.data.data;
  },

  /**
   * Update an agency's Stripe records
   */
  updateAgencyStripeRecords: async (
    agencyId: string,
    stripeCustomerId: string,
    stripeSubscriptionId: string,
    stripeStatus: string,
    planId?: number,
    markOnboardingCompleted?: boolean,
    brandingPolicy?: 'legacy' | 'always_on',
    manualAccessReason?: string
  ): Promise<AdminAgencyRecord> => {
    const response = await apiClient.patch<{ data: AdminAgencyRecord }>(
      `/admin/agencies/${agencyId}`,
      {
        stripeCustomerId,
        stripeSubscriptionId,
        stripeStatus,
        ...(planId ? { planId } : {}),
        ...(markOnboardingCompleted ? { markOnboardingCompleted: true } : {}),
        ...(brandingPolicy ? { brandingPolicy } : {}),
        ...(manualAccessReason !== undefined ? { manualAccessReason } : {}),
      }
    );
    
    return response.data.data;
  },

  /**
   * Reads the live Stripe state behind a subscription id without writing anything.
   * Backs the Sync button in the edit modal.
   */
  previewStripeSubscription: async (
    agencyId: string,
    subscriptionId: string
  ): Promise<StripeSubscriptionPreview> => {
    const response = await apiClient.get<{ data: StripeSubscriptionPreview }>(
      `/admin/agencies/${agencyId}/subscription-preview`,
      { params: { subscriptionId } }
    );

    return response.data.data;
  },

  /**
   * Toggles shadow access for backoffice users on the specified agency.
   */
  toggleShadowAccess: async (id: string, enabled: boolean): Promise<{ success: boolean; enabled: boolean }> => {
    const response = await apiClient.post(`/admin/agencies/${id}/shadow-access`, { enabled });
    return response.data;
  },

  /**
   * Delete an agency permanently
   */
  deleteAgency: async (id: string): Promise<void> => {
    await apiClient.delete(`/admin/agencies/${id}`);
  }
};
